import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import ImageItem from '../../Models/ImageItemModel';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { useTitle } from 'shouldve-been-react';
import { QuickTextInput, ErrorSnackbar, LoadDataBackdrop, ConfirmDialog } from 'shouldve-been-mui';
import type Folder from '../../Models/FolderModel';

type ImageItemSubTabProps = {
    imageItemId: string,
    closeSubTab: (successMessage: string | null) => void,
};

function ImageItemSubTab(props: Readonly<ImageItemSubTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Image Item');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const currentTheme: Theme = useTheme();

    const fileInputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);
    const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showConfirmDeletionDialog, setShowConfirmDeletionDialog] = useState<boolean>(false);
    const [showConfirmUploadDialog, setShowConfirmUploadDialog] = useState<boolean>(false);

    const [name, setName] = useState<string>('');
    const [createdDate, setCreatedDate] = useState<Date>(new Date(0));
    const [fileName, setFileName] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [imageBlob, setImageBlob] = useState<Blob | null>(null);
    const [folderId, setFolderId] = useState<string | null>(null);

    const [folders, setFolders] = useState<Folder[]>([]);

    const open: boolean = Boolean(anchorEl);

    const folder: Folder | null = useMemo<Folder | null>(() => folders.find((currentFolder: Folder) => currentFolder.folderId === folderId) ?? null, [folderId, folders]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => { //fetch data from indexedDB

        let mounted: boolean = true;
        
        async function fetchFromIndexedDB(): Promise<void> {

            const imageItem: ImageItem | null = await indexedDBContext.indexedDBService.getImageItem(props.imageItemId);
            const newFolders: Folder[] = await indexedDBContext.indexedDBService.getAllFolders();

            if (mounted) {
                if (imageItem === null) {
                    return;
                }

                setName(imageItem.name);
                setCreatedDate(imageItem.createdDate);
                setFileName(imageItem.fileName);
                setFolderId(imageItem.folderId);

                const newImageUrl: string | null = imageItem.imageBlob === null ? null : URL.createObjectURL(imageItem.imageBlob);
                setImageUrl(newImageUrl);
                setImageBlob(imageItem.imageBlob);

                setFolders(newFolders);

                setDataIsLoaded(true);
            }
        }

        void fetchFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, props.imageItemId]);

    useEffect(() => { //this cleanup is put in a separate useEffect so that it will be cleaned no matter how the objectURL was created (called two different places in this unit)

        return () => {
            if (imageUrl !== null) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [imageUrl]);

    async function handleImageSelected(e: React.ChangeEvent): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const files: FileList | null = (e.target as HTMLInputElement).files;

        const file: File | undefined = files?.[0];
    
        if (file === undefined) {
            setErrorSnackbarMessage('Failed to save image');
            return;
        }

        const newFileName: string = file.name;

        const newChangedDate: Date = new Date();

        const success: boolean = await indexedDBContext.indexedDBService.updateImageItem(new ImageItem(props.imageItemId, name, createdDate, newChangedDate, file, newFileName, folderId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save image');
            return;
        }

        const newImageUrl: string = URL.createObjectURL(file);

        setImageUrl(newImageUrl);
        setImageBlob(file);
        setFileName(newFileName);
    }

    async function handleNameChange(value: string): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const newChangedDate: Date = new Date();

        const success: boolean = await indexedDBContext.indexedDBService.updateImageItem(new ImageItem(props.imageItemId, value, createdDate, newChangedDate, imageBlob, fileName, folderId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save name');
            return;
        }
        
        setName(value);
    }

    async function handleDeleteImageItem(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const success: boolean = await indexedDBContext.indexedDBService.removeImageItem(props.imageItemId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete image');
            return;
        }

        props.closeSubTab('Image deleted');
    }

    async function handleSaveImageItem(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        //upload the folder first
        if (folder !== null) {

            if (folder.templateId !== null) {
                setErrorSnackbarMessage('Items in folders based on templates can only be uploaded as a whole. Individual items cannot be uploaded');
                return;
            }
            
            const syncSuccessFolder: boolean = await apiContext.api?.saveFolder(folder.folderId, folder.templateId, folder.name, folder.createdDate, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

            if (!syncSuccessFolder) {
                setErrorSnackbarMessage('Failed to upload the image\'s folder');
                return;
            }
        }

        const syncSuccess: boolean = await apiContext.api?.saveImageItem(name, fileName, createdDate, imageBlob, folderId, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload image');
            return;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeImageItem(props.imageItemId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete image (but it has been uploaded)');
            return;
        }

        props.closeSubTab('Image uploaded');
    }

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        onClick={(): void => props.closeSubTab(null)}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        {'Image "' + name + '"'}
                    </Typography>
                    <IconButton
                        onClick={(e: React.MouseEvent<HTMLElement>): void => setAnchorEl(e.currentTarget)}
                        sx={{
                            marginLeft: 'auto',
                            marginRight: '0px',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            color={currentTheme.palette.text.primary}  
                        />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '25px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
                position: 'relative',
            }}>
                <QuickTextInput
                    value={name}
                    onBlur={handleNameChange}
                    variant='outlined'
                    dense={true}
                    minWidth={Math.min(windowSize[0] * 0.8, 500)}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                    labelText='Name'
                    height='40px'
                />
                <br />
                <br />
                <div 
                    onClick={(): void => fileInputRef.current?.click()}
                    style={{
                        width: Math.min(windowSize[0] * 0.8, 500),
                        height: Math.min(windowSize[0] * 0.8, 500),
                        border: 'solid 1px',
                        borderColor: currentTheme.palette.text.primary,
                        borderRadius: '30px',
                        position: 'relative',
                        display: 'table',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        overflow: 'hidden',
                    }}
                >
                    {imageUrl === null
                        ? (
                            <p style={{
                                marginTop: '84px',
                                fontSize: '16px',
                                color: currentTheme.palette.text.primary,
                                fontWeight: 'bold',
                            }}>
                                Click to upload image
                            </p>
                        )
                        : (
                            <img
                                src={imageUrl}
                                alt='Image'
                                style={{
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    margin: 'auto',
                                }}
                            /> 
                        )
                    }
                </div>            
                <br />
                <br />

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={(): void => setAnchorEl(null)}
                    onClick={(): void => setAnchorEl(null)}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top',
                    }}
                    anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'bottom',
                    }}
                >
                    <MenuItem
                        onClick={(): void => setShowConfirmUploadDialog(true)}
                    >
                        Upload
                    </MenuItem>
                    <MenuItem
                        onClick={(): void => setShowConfirmDeletionDialog(true)}
                    >
                        Delete
                    </MenuItem>
                </Menu>

                {/*hidden input to allow uploading image when clicking on the div above*/}
                <input
                    type='file'
                    id='file'
                    accept='image/*'
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    onChange={handleImageSelected}
                />

                <ErrorSnackbar 
                    message={errorSnackbarMessage}
                    setMessage={setErrorSnackbarMessage}
                />

                <LoadDataBackdrop
                    dataIsLoaded={dataIsLoaded}
                    zIndex={1000}
                />
            </div>

            <ConfirmDialog 
                controller={showConfirmDeletionDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmDeletionDialog(false);
                    if (confirm) {
                        await handleDeleteImageItem();
                    }
                }}
                title='Delete image'
                text='Are you sure you want to delete this image?'
                noText='No'
                yesText='Yes'
                loadingConfirm={true}
            />

            <ConfirmDialog 
                controller={showConfirmUploadDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmUploadDialog(false);
                    if (confirm) {
                        await handleSaveImageItem();
                    }
                }}
                title='Upload image'
                text='Are you sure you want to upload this image? You can no longer edit it from this app.'
                noText='No'
                yesText='Yes'
                loadingConfirm={true}
            />
        </>
    );
}

export default ImageItemSubTab;